import type {I18n} from "@lingui/core";
import {getRandomId} from "../utils";
import createId, {IdType} from "./create-id";
import type {ModelId} from "./db";
import type {CategoryEntry, CategoryGroupId, GroupId, GroupRepo, ItemEntry} from "./GroupRepo";
import {getGroupRepoLoader} from "./GroupRepo";
import {fillInitialItems, fillInitialUnits} from "./initial-group-data";
import type {UserRepo} from "./UserRepo";

const importFromString = (str: string, repo: GroupRepo, catGroupId: CategoryGroupId) => {
  type Cat = {id: string; name: string; emoji?: string | null; sortIndex: string};
  type Item = {name: string; categoryId: string; emoji?: string | null};
  const {items, cats} = JSON.parse(str) as {cats: Cat[]; items: Item[]};
  const now = new Date();
  const catByOldId = new Map<string, CategoryEntry>();
  for (const c of cats) {
    const id = createId(IdType.Category);
    const cat: CategoryEntry = {
      id,
      name: c.name,
      emoji: c.emoji ?? null,
      createdAt: now,
    };
    catByOldId.set(c.id, cat);
    repo.Categories.set(id, cat);
    const catGroupEntryId = createId(IdType.CategoryGroupEntry);
    repo.CategoryGroupEntries.set(catGroupEntryId, {
      id: catGroupEntryId,
      categoryId: id,
      categoryGroupId: catGroupId,
      sortIndex: c.sortIndex,
    });
  }
  for (const i of items) {
    const cat = catByOldId.get(i.categoryId);
    const item: ItemEntry = {
      id: createId(IdType.Item),
      name: i.name,
      categoryId: cat?.id ?? null,
      createdAt: now,
      emoji: i.emoji ?? null,
    };
    repo.Items.set(item.id, item);
  }
};

export const createNewGroup = async (opts: {
  userRepo: UserRepo;
  name: string;
  i18n: I18n;
  withTemplate: boolean;
  importString: string | null;
}) => {
  const {userRepo, name, i18n, withTemplate, importString} = opts;
  const groupId = createId(IdType.Group);
  const shortId = getRandomId(8);
  const {id: userId} = userRepo.vals.get();
  // eslint-disable-next-line @typescript-eslint/require-await
  const group = await getGroupRepoLoader(groupId).withRepo(null, async (groupRepo) => {
    return groupRepo.store.transaction(() => {
      const now = new Date();
      groupRepo.vals.patch({
        id: groupId,
        shortId,
        name,
        creatorId: userId,
        createdAt: now,
      });
      groupRepo.Members.set(userId as unknown as ModelId<"members">, {
        id: userId,
        joinedAt: now,
        name: userRepo.vals.get().name,
      });
      const catGroupId = createId(IdType.CategoryGroup);
      groupRepo.CategoryGroups.set(catGroupId, {id: catGroupId, name: null});
      if (importString) {
        importFromString(importString, groupRepo, catGroupId);
      } else if (withTemplate) {
        fillInitialItems(groupRepo, i18n, catGroupId);
      }
      fillInitialUnits(groupRepo, i18n);
      const groupVals = {
        id: groupId,
        shortId,
        name,
        joinedAt: now,
      };
      userRepo.Group.set(groupId, groupVals);
      return groupVals;
    });
  });
  return group;
};

export const deleteGroup = (groupRepo: GroupRepo, userRepo: UserRepo, id: GroupId) => {
  userRepo.Group.delete(id);
  groupRepo.store.delTables();
  groupRepo.store.delValues();
};
